// View switching shared by the newsletter blocks that have an editor. A block's node view shows exactly
// one of three views at a time, each a container marked with data-view in the server template
// (dashboard/shared/tiptap/_<block>_editor.html.erb):
//
//   suggestions  the AI/other suggestions and a "Custom ..." button (only blocks that have suggestions)
//   editor       the form fields, with a Done button (and a Back link when there are suggestions)
//   preview      the server-rendered preview; its way back to the editor is the Edit link in the header
//                (dashboard/shared/tiptap/_block_actions.html.erb), shown only in this view
//
// Buttons inside those containers are wired here, so a block only supplies its rules:
//   [data-done]    -> preview, when canFinish() says the block has content; disabled otherwise
//   [data-edit]    -> editor
//   [data-back]    -> suggestions (shown only while hasSuggestions() and the block has never been in preview)
//   [data-custom]  -> editor, focusing the [data-autofocus] field
//
//   const views = blockViews(dom, {
//     canFinish: () => Boolean(currentNode.attrs.text),
//     hasSuggestions: () => suggestions.length > 0,
//     onChange: view => { if (view === 'preview') schedulePreview(); },
//   });
//   views.refresh();   // after anything that can change canFinish()/hasSuggestions()

export const VIEWS = ['suggestions', 'editor', 'preview'];

// A block with content opens in its preview, a new one with suggestions in the suggestions, otherwise in the editor.
export function initialView({ hasContent, hasSuggestions }) {
  if (hasContent) return 'preview';
  return hasSuggestions ? 'suggestions' : 'editor';
}

export function blockViews(root, { canFinish = () => true, hasSuggestions = () => false, onChange } = {}) {
  const sections = {};
  VIEWS.forEach(name => { sections[name] = root.querySelector(`[data-view="${name}"]`); });
  const doneButton = root.querySelector('[data-done]');
  const backLink = root.querySelector('[data-back]');
  const editLink = root.querySelector('[data-edit]');

  let current = null;
  let everPreviewed = false;

  const refresh = () => {
    if (doneButton) doneButton.disabled = !canFinish();
    backLink?.classList.toggle('hidden', !(hasSuggestions() && !everPreviewed));
    editLink?.classList.toggle('hidden', current !== 'preview');
  };

  // A view the block doesn't have (no suggestions, no preview) falls back to the editor.
  const setView = (name, { focus = false } = {}) => {
    const view = sections[name] ? name : 'editor';
    if (view === 'preview') everPreviewed = true;
    VIEWS.forEach(other => sections[other]?.classList.toggle('hidden', other !== view));
    const changed = view !== current;
    current = view;
    root.setAttribute('data-block-view', view);
    refresh();
    if (focus && view === 'editor') sections.editor?.querySelector('[data-autofocus]')?.focus();
    if (changed) onChange?.(view);
  };

  doneButton?.addEventListener('click', () => { if (canFinish()) setView('preview'); });
  editLink?.addEventListener('click', e => {
    e.preventDefault();
    setView('editor');
  });
  backLink?.addEventListener('click', () => setView('suggestions'));
  root.querySelectorAll('[data-custom]').forEach(button => {
    button.addEventListener('click', () => setView('editor', { focus: true }));
  });

  return {
    get view() { return current; },
    setView,
    showSuggestions: () => setView('suggestions'),
    showEditor: options => setView('editor', options),
    showPreview: () => setView('preview'),
    refresh,
  };
}
